/**
 * @ComponentFor ShopTheLookItemBlockViewModel
 */
import React, { useState } from 'react';
import { styled, StyledProps } from '@glitz/react';
import connect from 'Shared/connect';
import { isCompact } from 'Shared/Viewport';
import ShopTheLookItemBlock from './ShopTheLookItemBlockViewModel.type';
import { SolidButton, ButtonTheme } from 'Shared/TextButtons';
import { Plus12x12 } from 'Shared/Icons/Plus';
import { HEIGHT as mobileHeaderHeight } from 'SiteLayout/Header/MobileHeader';
import { HEIGHT as desktopHeaderHeight } from 'SiteLayout/Header/DesktopHeader';
import { AspectRatioInner, AspectRatioOuter } from 'Shared/SharedComponents/atoms/AspectRatio';
import ProductListItemViewModel from 'Product/ProductListing/ProductListItemViewModel.type';
import * as style from 'Shared/Style';
import { calculateTopPadding, Ratio } from 'Shared/AspectRatio';
import { setImageBackground, Preset } from 'Shared/image-background';

type ConnectedPropType = {
  currentBreakpoint: number;
};

type PropType = ShopTheLookItemBlock & ConnectedPropType & StyledProps & {
  setModalData: (value: { showModal: boolean, products: ProductListItemViewModel[] }) => void;
};

const ImageContainer = styled(AspectRatioInner, {
  background: {
    size: 'cover',
    position: 'center center',
  },
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'end'
})

const Label = styled.span({
  color: style.colors.monochrome.white,
  display: 'block',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '100%',
  letterSpacing: '0.01em',
  textTransform: 'uppercase',
})

const Content = styled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingBottom: '12px',
  gap: '8px',
  ['@media ' + style.mediaMinLarge]: {
    gap: '32px',
    flexDirection: 'column',
    paddingBottom: '24px',
  },
})

const Wrap = styled(AspectRatioOuter, {
  borderRadius: '8px',
  paddingTop: calculateTopPadding(Ratio.ThreeToFour),
  ['@media ' + style.mediaUpToLarge]: {
    marginBottom: '8px',
  },
})

const TextLabel = styled(Label, {
  ['@media ' + style.mediaUpToLarge]: {
    textAlign: 'center',
  },
})

const Plus = styled(Plus12x12, {
  marginLeft: '6px',
  ['@media ' + style.mediaMinLarge]: {
    marginLeft: '8px'
  },
})

function ShopTheLookItemBlock(props: PropType & { currentBreakpoint: number; onClick?: () => void }) {
  const { currentBreakpoint, buttonText, label, products } = props;
  const isMobile = isCompact(currentBreakpoint);
  isMobile ? mobileHeaderHeight : desktopHeaderHeight;

  // 3:4 ratio
  return (
    <Wrap onClick={props.onClick}>
      <ImageContainer css={{ background: { image: setImageBackground(props.image?.url, Preset.HalfWidth), }, }}>
        <Content>
          <TextLabel>{label}</TextLabel>
          {buttonText && products?.length > 0 && (
            <SolidButton
              text={`${isMobile ? '' : buttonText + ' '}(${products?.length ?? 0})`}
              padding={isMobile ? { x: 11 } : undefined}
              iconAfter={<Plus />}
              onClick={() => props.setModalData({ showModal: true, products: props.products })}
              theme={ButtonTheme.TransparentWhite}
            />
          )}
        </Content>
      </ImageContainer>
    </Wrap>
  );
}

export default connect(
  state => ({
    currentBreakpoint: state.currentBreakpoint,
  })
)(styled(ShopTheLookItemBlock));